import React, { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";

export default function AdminComicsManager({ comics, onAdd, onToggle, onDelete }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [displayOrder, setDisplayOrder] = useState(1);
  const [busy, setBusy] = useState(false);

  const remaining = 10 - comics.length;
  const sortedComics = useMemo(
    () => [...comics].sort((a, b) => a.display_order - b.display_order),
    [comics],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    await onAdd(file, title, displayOrder);
    setBusy(false);
    setFile(null);
    setTitle("");
    e.target.reset?.();
  };

  return (
    <div className="space-y-6" data-testid="admin-comics-tab">
      <div className="bb-card p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-serif text-2xl text-[var(--bb-forest)]">Comics gallery</h3>
            <p className="text-xs text-[var(--bb-muted)] mt-1">Up to 10 comics shown inside the hero "Explore Comics" modal. JPG / PNG / WebP · 2MB.</p>
          </div>
          <div className="bb-tag">{comics.length}/10 used · {remaining} left</div>
        </div>
        {remaining > 0 ? (
          <form onSubmit={submit} className="grid md:grid-cols-4 gap-3 items-end" data-testid="admin-comic-add">
            <div className="md:col-span-2">
              <label className="bb-overline text-[var(--bb-muted)]">Image</label>
              <input required type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="mt-2 w-full text-xs" />
            </div>
            <div>
              <label className="bb-overline text-[var(--bb-muted)]">Title (optional)</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm" />
            </div>
            <div>
              <label className="bb-overline text-[var(--bb-muted)]">Order (1–10)</label>
              <input type="number" min={1} max={10} value={displayOrder} onChange={(e) => setDisplayOrder(parseInt(e.target.value || "1", 10))} className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm" />
            </div>
            <button type="submit" disabled={busy || !file} className="bb-btn-primary text-sm md:col-span-4 md:justify-self-start">{busy ? "Uploading…" : "Add comic"}</button>
          </form>
        ) : (
          <p className="text-xs text-[var(--bb-muted)]">Limit reached. Remove a comic to add another.</p>
        )}
      </div>

      {comics.length === 0 ? (
        <div className="bb-card p-10 text-center text-sm text-[var(--bb-muted)]">No comics yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4">
          {sortedComics.map((c) => (
            <div key={c.comic_id} className="bb-card p-3" data-testid={`admin-comic-${c.comic_id}`}>
              <div className="aspect-[3/4] bg-[var(--bb-sand)] rounded overflow-hidden mb-2 relative">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.image_path}`} alt="" className="w-full h-full object-cover" />
                <span className={`absolute top-1 right-1 bb-tag text-[10px] ${c.is_active ? "bb-tag-green" : ""}`}>{c.is_active ? "Active" : "Hidden"}</span>
                <span className="absolute bottom-1 left-1 bb-tag text-[10px]">#{c.display_order}</span>
              </div>
              <div className="text-xs line-clamp-1 mb-1">{c.title || "Untitled"}</div>
              <div className="flex items-center justify-between text-xs">
                <button onClick={() => onToggle(c)} className="bb-link" data-testid={`comic-toggle-${c.comic_id}`}>{c.is_active ? "Hide" : "Show"}</button>
                <button onClick={() => onDelete(c.comic_id)} className="text-[var(--bb-error)] hover:underline inline-flex items-center gap-1" data-testid={`comic-delete-${c.comic_id}`}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
